import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useConfirm } from '../../lib/confirm'
import { titleCase } from '../../lib/format'
import { Alert, Button, Card, CardBody, CardHeader, Input, Select, Skeleton, Textarea } from '../../components/ui'

/* Faithful port of anansi/app/templates/admin/role_catalog_edit.html + its
 * view admin/routes.py:role_catalog_edit(). */

const LICENCE_TYPES = ['artisanal', 'small_scale', 'exploration', 'processing', 'dealer', 'trader'] as const
const SCOPES = [
  { value: 'mining', label: 'Mining (statutory)' },
  { value: 'operational', label: 'Operational (industry practice)' },
  { value: 'coop_governance', label: 'Cooperative governance' },
]
const REQUIREMENT_CHOICES = [
  { value: 'mandatory', label: 'Mandatory' },
  { value: 'recommended', label: 'Recommended' },
  { value: 'not_applicable', label: 'Not applicable' },
]

interface CatalogRole {
  key: string
  title: string
  scope: string
  statute_ref?: string
  description?: string
  conditional_note?: string
  applies_to_licence_types: Record<string, string>
  applies_to_org_types: string[]
  allow_one_person_multi_role: boolean
  min_count: number
  active: boolean
}
interface RoleResponse { ok: boolean; role: CatalogRole }

export default function AdminRoleCatalogEdit() {
  const { roleKey } = useParams<{ roleKey: string }>()
  const qc = useQueryClient()
  const confirm = useConfirm()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'role-catalog', roleKey],
    queryFn: () => api.get<RoleResponse>(`/role-catalog/${roleKey}`),
    enabled: Boolean(roleKey),
  })

  const [requirements, setRequirements] = useState<Record<string, string> | null>(null)
  const [orgTypes, setOrgTypes] = useState<string[] | null>(null)

  const role = data?.role
  const reqState = requirements ?? role?.applies_to_licence_types ?? {}
  const orgTypeState = orgTypes ?? role?.applies_to_org_types ?? []

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put<RoleResponse>(`/role-catalog/${roleKey}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'role-catalog'] }),
  })
  const deactivate = useMutation({
    mutationFn: () => api.post<RoleResponse>(`/role-catalog/${roleKey}/deactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'role-catalog'] }),
  })

  if (!roleKey) return <Navigate to="/role-catalog" replace />
  if (isError) {
    return (
      <Alert tone="danger">
        {(error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/role-catalog">Back to catalog</Link></div>
      </Alert>
    )
  }
  if (isLoading || !role) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={200} /></div>

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (orgTypeState.length === 0) {
      alert('At least one organisation type (company or cooperative) is required.')
      return
    }
    const form = new FormData(e.currentTarget)
    save.mutate({
      title: String(form.get('title') ?? '').trim(),
      scope: String(form.get('scope') ?? ''),
      statute_ref: String(form.get('statute_ref') ?? '').trim(),
      description: String(form.get('description') ?? '').trim(),
      conditional_note: String(form.get('conditional_note') ?? '').trim(),
      applies_to_licence_types: reqState,
      applies_to_org_types: orgTypeState,
      min_count: Number(form.get('min_count') ?? 1),
      allow_one_person_multi_role: form.get('allow_one_person_multi_role') === 'on',
      active: form.get('active') === 'on',
    })
  }

  function toggleOrgType(type: string, checked: boolean) {
    const current = orgTypeState
    setOrgTypes(checked ? [...current, type] : current.filter((t) => t !== type))
  }

  return (
    <>
      <div style={{ marginBottom: 'var(--sp-4)' }}>
        <Link to="/role-catalog">← Back to catalog</Link>
      </div>

      <div className="page-head">
        <div>
          <h1>{role.title}</h1>
          <p className="page-head__sub" style={{ fontFamily: 'monospace' }}>{role.key}</p>
        </div>
      </div>

      {save.isError && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert tone="danger">{save.error instanceof ApiError ? save.error.message : 'Could not save this role.'}</Alert>
        </div>
      )}

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardBody>
          <form onSubmit={onSubmit} className="stack">
            <div className="grid grid--2">
              <Input label="Title" name="title" required defaultValue={role.title} />
              <Select label="Scope" name="scope" defaultValue={role.scope}>
                {SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </div>
            <Input label="Statute Reference" name="statute_ref" defaultValue={role.statute_ref} />
            <Textarea label="Description" name="description" rows={2} defaultValue={role.description} />
            <Textarea label="Conditional Note" name="conditional_note" rows={2} defaultValue={role.conditional_note} />

            <div>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--sp-2)' }}>Requirement by licence type</p>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr><th>Licence Type</th>{REQUIREMENT_CHOICES.map((r) => <th key={r.value}>{r.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {LICENCE_TYPES.map((lt) => (
                      <tr key={lt}>
                        <td>{titleCase(lt)}</td>
                        {REQUIREMENT_CHOICES.map((r) => (
                          <td key={r.value} style={{ textAlign: 'center' }}>
                            <input
                              type="radio" name={`req_${lt}`} value={r.value}
                              checked={(reqState[lt] || 'not_applicable') === r.value}
                              onChange={() => setRequirements({ ...reqState, [lt]: r.value })}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--sp-2)' }}>Applies to</p>
              <div className="row" style={{ gap: 'var(--sp-4)' }}>
                <label className="row" style={{ gap: 6 }}>
                  <input type="checkbox" checked={orgTypeState.includes('company')}
                    onChange={(e) => toggleOrgType('company', e.target.checked)} /> Company
                </label>
                <label className="row" style={{ gap: 6 }}>
                  <input type="checkbox" checked={orgTypeState.includes('cooperative')}
                    onChange={(e) => toggleOrgType('cooperative', e.target.checked)} /> Cooperative
                </label>
              </div>
            </div>

            <div className="grid grid--2">
              <Input label="Minimum Count" name="min_count" type="number" min={1} max={20} defaultValue={role.min_count || 1} />
              <div className="stack" style={{ gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
                <label className="row" style={{ gap: 6 }}>
                  <input type="checkbox" name="allow_one_person_multi_role" defaultChecked={role.allow_one_person_multi_role} />
                  Allow one person to hold multiple roles
                </label>
                <label className="row" style={{ gap: 6 }}>
                  <input type="checkbox" name="active" defaultChecked={role.active} /> Active
                </label>
              </div>
            </div>

            <div className="row" style={{ gap: 'var(--sp-2)' }}>
              <Button type="submit" loading={save.isPending} confirm={{ title: "Save these changes?" }}>Save</Button>
              <Link to="/role-catalog" className="btn btn--ghost">Cancel</Link>
            </div>
          </form>
        </CardBody>
      </Card>

      {role.active && (
        <Card>
          <CardHeader title="Deactivate this role" />
          <CardBody>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 'var(--sp-3)' }}>
              Deactivated roles stay in the catalog but stop counting toward compliance coverage.
            </p>
            <Button
              variant="danger" loading={deactivate.isPending}
              onClick={async () => {
                if (await confirm({ title: `Deactivate '${role.title}'?`, destructive: true })) deactivate.mutate()
              }}
            >
              Deactivate role
            </Button>
          </CardBody>
        </Card>
      )}
    </>
  )
}
